/**
 * Formatting + status helpers for the Invoicing section (InvoicesPage,
 * InvoiceDetailPage, ClientsPage, InvoiceSettingsPage). Non-JSX and
 * dependency-free, mirroring marketsFormat.ts.
 *
 * Money/decimal fields arrive from glorify_ai_axum as JSON STRINGS (e.g.
 * "123.45"), not numbers, to avoid float-precision drift on accounting
 * values. `formatAud()` is the only place that parses one for display —
 * everything sent back to the API keeps using the string form.
 */

const AUD_FORMATTER = new Intl.NumberFormat('en-AU', {
  style: 'currency',
  currency: 'AUD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

/** Formats a decimal-as-string money field ("123.45") as "$123.45". Blank/non-numeric input renders "—". */
export function formatAud(value: string | null | undefined): string {
  if (value === null || value === undefined || value.trim() === '') return '—'
  const parsed = Number(value)
  if (!Number.isFinite(parsed)) return '—'
  return AUD_FORMATTER.format(parsed)
}

/** Formats a decimal-as-string percentage field ("0.1000") as "10.00%". */
export function formatPercentFromDecimalString(value: string | null | undefined): string {
  if (value === null || value === undefined || value.trim() === '') return '—'
  const parsed = Number(value)
  if (!Number.isFinite(parsed)) return '—'
  return `${(parsed * 100).toFixed(2)}%`
}

/** Formats a "YYYY-MM-DD" date string. Parsed as local calendar date (not UTC midnight) to avoid an off-by-one day. */
export function formatDate(isoDate: string | null | undefined): string {
  if (!isoDate) return '—'
  const [y, m, d] = isoDate.split('-').map(Number)
  if (!y || !m || !d) return isoDate
  try {
    return new Date(y, m - 1, d).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  } catch {
    return isoDate
  }
}

/** Formats an RFC3339 timestamp string for display. */
export function formatTimestamp(isoString: string | null | undefined): string {
  if (!isoString) return '—'
  try {
    return new Date(isoString).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' })
  } catch {
    return isoString
  }
}

/** Today as "YYYY-MM-DD", for defaulting date <input> fields. */
export function isoToday(): string {
  return new Date().toISOString().slice(0, 10)
}

/** "YYYY-MM-DD" `days` from today, for defaulting a due-date input from payment terms. */
export function isoDaysFromToday(days: number): string {
  const d = new Date()
  d.setDate(d.getDate() + days)
  return d.toISOString().slice(0, 10)
}

// ─── Status ─────────────────────────────────────────────────────────────────
// 0=draft, 1=sent, 2=paid, 3=overdue, 4=void — frozen contract, see App.tsx /
// glorify.ts header comments.

export type InvoiceStatusId = 0 | 1 | 2 | 3 | 4

export const INVOICE_STATUSES: { id: InvoiceStatusId; label: string }[] = [
  { id: 0, label: 'Draft' },
  { id: 1, label: 'Sent' },
  { id: 2, label: 'Paid' },
  { id: 3, label: 'Overdue' },
  { id: 4, label: 'Void' },
]

const STATUS_LABELS: Record<InvoiceStatusId, string> = {
  0: 'Draft',
  1: 'Sent',
  2: 'Paid',
  3: 'Overdue',
  4: 'Void',
}

const STATUS_CHIP_VARIANT: Record<InvoiceStatusId, string> = {
  0: 'gd-status-chip--draft',
  1: 'gd-status-chip--sent',
  2: 'gd-status-chip--paid',
  3: 'gd-status-chip--overdue',
  4: 'gd-status-chip--void',
}

/** Legal next states for a status transition, per the frozen backend contract. Empty = terminal state. */
const LEGAL_TRANSITIONS: Record<InvoiceStatusId, InvoiceStatusId[]> = {
  0: [1], // draft -> sent
  1: [2, 3, 4], // sent -> paid | overdue | void
  2: [], // paid is terminal
  3: [2, 4], // overdue -> paid | void
  4: [], // void is terminal
}

function isValidStatusId(statusId: number): statusId is InvoiceStatusId {
  return statusId === 0 || statusId === 1 || statusId === 2 || statusId === 3 || statusId === 4
}

export function invoiceStatusLabel(statusId: number): string {
  return isValidStatusId(statusId) ? STATUS_LABELS[statusId] : `Unknown (${statusId})`
}

/** Full class string for the chip, e.g. "gd-status-chip gd-status-chip--paid". */
export function invoiceStatusChipClass(statusId: number): string {
  const variant = isValidStatusId(statusId) ? STATUS_CHIP_VARIANT[statusId] : 'gd-status-chip--draft'
  return `gd-status-chip ${variant}`
}

/** Only draft invoices allow header/line-item edits (server enforces this too — 400 otherwise). */
export function isDraft(statusId: number): boolean {
  return statusId === 0
}

export function legalNextStatuses(statusId: number): { id: InvoiceStatusId; label: string }[] {
  const next = isValidStatusId(statusId) ? LEGAL_TRANSITIONS[statusId] : []
  return next.map((id) => ({ id, label: STATUS_LABELS[id] }))
}
